import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AlertCircle, ShieldOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Map route paths to page IDs
const ROUTE_TO_PAGE_ID = {
  "/attendance": "attendance_dashboard",
  "/attendance/punch": "attendance_punch",
  "/attendance/records": "attendance_records",
  "/attendance/reports": "attendance_reports",
  "/attendance/leave": "attendance_leave",
  "/attendance/staff": "attendance_staff",
  "/attendance/salary": "attendance_salary",
  "/attendance/settings": "attendance_settings",
};
// /attendance/location-tracking is intentionally unmapped here: that page checks
// access itself against the server (admins or the "location_tracking" permission).

const AttendanceProtectedRoute = ({ children }) => {
  const [accessGranted, setAccessGranted] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState("");
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // First check if user is authenticated
        const authResponse = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
        });
        
        if (!authResponse.ok) {
          setAccessGranted(false);
          setLoading(false);
          return;
        }
        
        const userData = await authResponse.json();
        setUserRole(userData.role);
        
        // Super admin and admin always have access
        if (userData.role === "super_admin" || userData.role === "admin") {
          setAccessGranted(true);
          setLoading(false);
          return;
        }
        
        // Get current route's page ID
        const currentPath = location.pathname;
        const pageId = ROUTE_TO_PAGE_ID[currentPath];
        
        // If no pageId mapping, allow access (e.g., for nested routes)
        if (!pageId) {
          setAccessGranted(true);
          setLoading(false);
          return;
        }
        
        // Check attendance access permissions
        const accessResponse = await fetch(`${BACKEND_URL}/api/attendance/my-access`, {
          credentials: "include",
        });
        
        if (accessResponse.ok) {
          const accessData = await accessResponse.json();
          const allowedPages = accessData.pages || [];
          setAccessGranted(allowedPages.includes(pageId));
        } else {
          // If access check fails, default to allowing (for backwards compatibility)
          setAccessGranted(true);
        }
      } catch (error) {
        console.error("Error checking access:", error);
        // On error, default to allowing access
        setAccessGranted(true);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!accessGranted) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[500px]">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-20 h-20 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <ShieldOff className="w-10 h-10 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Access Denied
          </h2>
          <p className="text-slate-600 dark:text-slate-400" data-testid="access-denied-message">
            You do not have permission to access this page.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            Please contact your administrator if you believe this is an error.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
};

export default AttendanceProtectedRoute;
